import { flowUseCase } from "./bookingFlowUseCase";
import { isRoomFamily } from "./useCases";

/**
 * WHICH customer-data fields a booking requires — the single definition.
 *
 * Sibling of `personalDataRules.ts`, which answers a different question: that
 * file says *how rigorously* a field is validated once it is required ("required
 * ⇒ rigorously validated"); this one says *which* fields are required at all.
 *
 * ── Why this file exists ──────────────────────────────────────────────────
 *
 * The answer was decided in TWO places that did not agree, and the disagreement
 * was live:
 *
 *   - `PersonalDataStepShared`'s `requiredFields` prop drove the asterisks and
 *     `aria-required` on the inputs, and nothing else.
 *   - `isPersonalDataValid` (bookingCreation slice) decided whether the step
 *     could be LEFT, keying on the Redux `useCase` and hardcoding
 *     address + zipCode + city + country for `"room"` and `"venue"`.
 *
 * So #1926's covered-booking narrowing removed the asterisks from the address
 * block and the Next button stayed disabled anyway: a member of an organization
 * whose space agreement covers the booking still could not complete it without
 * typing a private postal address. That is the innodom launch case, and the
 * feature read as shipped while not working.
 *
 * Per CLAUDE.md's "One Rule, One Place": the form renders from this set and the
 * gate checks this set. A second surface imports it; it does not restate it.
 *
 * ── Why a covered booking asks for less ───────────────────────────────────
 *
 * A covered booking produces no invoice to the person — the organization is
 * billed under its space agreement — so a billing address is data nobody will
 * ever use (PO, #1926). The constraint underneath is legal, not cosmetic: a
 * §14-UStG-compliant invoice needs address + postcode + city
 * (`frontend/admin-ui/src/utils/invoiceValidation.ts`). The rule is therefore
 * "required iff this booking invoices THIS person" — derived, never a free
 * per-tenant toggle, which is what keeps #1401's tenant-configurable field
 * catalogue from being able to switch off something the tax office needs.
 *
 * ── The `useCase` values are the REDUX ones ───────────────────────────────
 *
 * `"room"`, not `"apartment"`. The Redux `useCase` for the room-rental flow is
 * set to `"room"` by `useBookingFlowLifecycle`, while the Prisma enum and the
 * API say `apartment`. The value is resolved by `flowUseCase`
 * (`bookingFlowUseCase.ts`), the one place that translates the alias — and
 * that treats a raw `"apartment"` as no flow, so it keeps falling into the
 * default set as it always did.
 */

/** Every field whose required-ness can differ between flows. */
export type PersonalDataFieldKey =
  | "firstName"
  | "lastName"
  | "phone"
  | "email"
  | "company"
  | "address"
  | "country"
  | "city"
  | "zipCode"
  | "uscCustomerId";

/**
 * Checked on every flow regardless of the set below, so they are deliberately
 * NOT part of it:
 *   - `uscCustomerId` is gated on the runtime `partner === "usc"` signal and is
 *     `undefined` (⇒ valid) for everyone else;
 *   - `isAgreed` is a use-case rule (`rental` only), not a field the customer
 *     can be asked for more or less of.
 */

/** The base every booking needs in order to reach the right human. */
const BASE: readonly PersonalDataFieldKey[] = ["firstName", "lastName", "email"];

/**
 * A covered booking narrows to the base (#1926). The address fields stay
 * VISIBLE and fillable — they are simply not demanded.
 */
const COVERED: readonly PersonalDataFieldKey[] = BASE;

/**
 * Room and venue.
 *
 * `company` was carried here because the pre-existing gate ANDed it, and since
 * `validation.company` is vacuously true that preserved the BUTTON exactly. It
 * did not preserve the SCREEN: this same set drives `isRequired`, so the field
 * wore a required asterisk while nothing enforced it, and a guest booking an
 * apartment was asked for a company name they do not have (#2948, reported from
 * production).
 *
 * Removing it cannot change the gate — `every` over the set drops a term that
 * is always true — and it takes the asterisk off a field that was never
 * demanded. Which fields a tenant wants to demand is the next step, per
 * category; this is the stop-gap that takes the untrue claim off the screen.
 */
const ROOM_AND_VENUE: readonly PersonalDataFieldKey[] = [
  ...BASE,
  "phone",
  "address",
  "country",
  "city",
  "zipCode",
];

/** Peer-to-peer handover and key handover do not ask for a phone number. */
const HANDOVER: readonly PersonalDataFieldKey[] = BASE;

/** Everything else: the base plus a reachable phone number. */
const DEFAULT: readonly PersonalDataFieldKey[] = [...BASE, "phone"];

export interface RequiredFieldsOptions {
  /**
   * Is this booking covered by the customer's organization? Sourced from the
   * availability response (`coveredForMe`) via `useIsCoveredBooking` — the app
   * never decides it itself, because the server re-derives the same predicate
   * when it creates the order.
   */
  covered?: boolean;
}

/**
 * The fields this booking requires.
 *
 * `useCase` is the Redux one (see the header). An unknown or absent use case
 * resolves to `DEFAULT`, which is the conservative direction: it asks for a
 * phone number rather than silently asking for nothing.
 */
export const requiredPersonalDataFields = (
  useCase: string | null | undefined,
  { covered = false }: RequiredFieldsOptions = {}
): ReadonlySet<PersonalDataFieldKey> => {
  if (useCase === "p2p" || useCase === "keys") return new Set(HANDOVER);
  if (isRoomFamily(flowUseCase(useCase))) {
    return new Set(covered ? COVERED : ROOM_AND_VENUE);
  }
  // Coverage is a room/venue concept today — it is granted per object through a
  // space agreement, and no pooled flow reads `coveredForMe`. Ignoring it here
  // rather than narrowing every flow keeps this honest: if a pooled use case
  // ever becomes coverable, this is the line that has to change.
  return new Set(DEFAULT);
};
